import { c as _c } from "react/compiler-runtime";
import React from "react";
import { useDispatch, useSelector } from "react-redux";
import { NumericFormat } from "react-number-format";
import { usePlausible } from "next-plausible";
import { PortableText } from "@portabletext/react";
import { Dispatch } from "@reduxjs/toolkit";
import AnimateHeight from "react-animate-height";
import { Pane, PaneContainer, PaneTitle } from "../Panes.style";
import { ActionBar, AmountInputSection, InfoParagraph, ShareContainer, ShareInputContainer, ShareLink, ShareSelectionSpacer, ShareSelectionWrapper, SharesSelectorContainer, ShowAllOrganizationsLink, SumButtonsWrapper, SumWrapper } from "./SingleCauseAreaPane.style";
import { NextButton } from "../../shared/Buttons/NavigationButtons";
import { ToolTip } from "../../shared/ToolTip/ToolTip";
import { Spinner } from "../../../../Spinner/Spinner";
import { RadioButtonGroup } from "../../../../RadioButton/RadioButtonGroup";
import { EffektButton, EffektButtonVariant } from "../../../../EffektButton/EffektButton";
import { State } from "../../../store/state";
import { RecurringDonation, ShareType } from "../../../types/Enums";
import { setRecurring, setSum, setCauseAreaAmount, setOrgAmount, setCauseAreaDistributionType, setShowAllOrganizations, setHasManuallyEditedPrefilledOrgAmount } from "../../../store/donation/actions";
import { nextPane } from "../../../store/layout/actions";
import { DonationActionTypes } from "../../../store/donation/types";
import { LayoutActionTypes } from "../../../store/layout/types";
import { thousandize } from "../../../../../../../util/formatting";
import { useAmountCalculation } from "../AmountPane/useAmountCalculation";
import { AmountContext, SmartDistributionContext } from "../../../types/WidgetProps";
import { useIsMobile } from "../../../../../../../hooks/useIsMobile";
interface SingleCauseAreaPaneProps {
  nextButtonText: string;
  enableRecurring: boolean;
  enableSingle: boolean;
  singleDonationText: string;
  monthlyDonationText: string;
  amountContext: AmountContext;
  smartDistributionContext: SmartDistributionContext;
}

/**
 * Amount pane used when a platform has a single (active) cause area, e.g. Norway.
 *
 * It reproduces the pre-rewrite ("main") widget experience — recurring toggle,
 * suggested sums, a smart/custom distribution selector with an explanatory
 * description — using main's markup and styling, but is driven by the rewritten
 * donation state. The one thing carried over from the new widget is that custom
 * organization distribution is entered as direct kroner amounts rather than
 * percentages. Operations/tip is intentionally omitted to match the old widget.
 */
export const SingleCauseAreaPane: React.FC<SingleCauseAreaPaneProps> = t0 => {
  const $ = _c(87);
  const {
    nextButtonText,
    enableRecurring,
    enableSingle,
    singleDonationText,
    monthlyDonationText,
    amountContext,
    smartDistributionContext
  } = t0;
  const dispatch = useDispatch();
  const plausible = usePlausible();
  const isMobile = useIsMobile();
  const causeAreas = useSelector(_temp);
  const {
    recurring,
    causeAreaAmounts: t1,
    orgAmounts: t2,
    causeAreaDistributionType: t3,
    prefilledShares: t4,
    showAllOrganizations: t5,
    hasManuallyEditedPrefilledOrgAmount: t6
  } = useSelector(_temp2);
  let t7;
  if ($[0] !== t1) {
    t7 = t1 === undefined ? {} : t1;
    $[0] = t1;
    $[1] = t7;
  } else {
    t7 = $[1];
  }
  const causeAreaAmounts = t7;
  let t8;
  if ($[2] !== t2) {
    t8 = t2 === undefined ? {} : t2;
    $[2] = t2;
    $[3] = t8;
  } else {
    t8 = $[3];
  }
  const orgAmounts = t8;
  let t9;
  if ($[4] !== t3) {
    t9 = t3 === undefined ? {} : t3;
    $[4] = t3;
    $[5] = t9;
  } else {
    t9 = $[5];
  }
  const causeAreaDistributionType = t9;
  const prefilledShares = t4 === undefined ? null : t4;
  const showAllOrganizations = t5 === undefined ? false : t5;
  const hasManuallyEditedPrefilledOrgAmount = t6 === undefined ? false : t6;
  const activeCauseAreas = causeAreas?.filter(_temp3) ?? [];
  const causeArea = activeCauseAreas[0];
  let t10;
  if ($[6] !== causeAreas) {
    t10 = causeAreas ?? [];
    $[6] = causeAreas;
    $[7] = t10;
  } else {
    t10 = $[7];
  }
  const {
    totalAmount
  } = useAmountCalculation("single", causeArea?.id ?? null, t10);
  const currentAmount = causeArea ? causeAreaAmounts[causeArea.id] || 0 : 0;
  const inputValue = currentAmount;
  const [showErrors, setShowErrors] = React.useState(false);
  let t11;
  if ($[8] !== prefilledShares) {
    t11 = !!prefilledShares && Object.keys(prefilledShares).length > 0;
    $[8] = prefilledShares;
    $[9] = t11;
  } else {
    t11 = $[9];
  }
  const hasPrefilledOrgs = t11;
  const isTrackingPrefilledShares = hasPrefilledOrgs && !hasManuallyEditedPrefilledOrgAmount;
  let t12;
  if ($[10] !== currentAmount || $[11] !== isTrackingPrefilledShares || $[12] !== orgAmounts || $[13] !== prefilledShares) {
    t12 = orgId => {
      const share = prefilledShares?.[orgId];
      if (isTrackingPrefilledShares && share !== undefined) {
        return Math.round(share / 100 * currentAmount);
      }
      return orgAmounts[orgId] || 0;
    };
    $[10] = currentAmount;
    $[11] = isTrackingPrefilledShares;
    $[12] = orgAmounts;
    $[13] = prefilledShares;
    $[14] = t12;
  } else {
    t12 = $[14];
  }
  const displayOrgAmount = t12;
  const effectiveTotalAmount = isTrackingPrefilledShares ? currentAmount : totalAmount;
  if (!causeAreas) {
    let t13;
    if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
      t13 = <Pane><PaneContainer><div style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "100%"
          }}><Spinner /></div></PaneContainer></Pane>;
      $[15] = t13;
    } else {
      t13 = $[15];
    }
    return t13;
  }
  if (!causeArea) {
    let t13;
    if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
      t13 = <Pane><PaneContainer><div>Missing cause area</div></PaneContainer></Pane>;
      $[16] = t13;
    } else {
      t13 = $[16];
    }
    return t13;
  }
  const activeOrganizations = causeArea.organizations.filter(_temp4).sort(_temp5);
  const hasMultipleOrgs = activeOrganizations.length > 1;
  const distributionType = causeAreaDistributionType[causeArea.id] ?? ShareType.STANDARD;
  const hideAmountInput = hasMultipleOrgs && distributionType === ShareType.CUSTOM && (!hasPrefilledOrgs || hasManuallyEditedPrefilledOrgAmount);
  const suggestedSums = recurring ? amountContext.preset_amounts_recurring : amountContext.preset_amounts_single;
  const setAmount = amount => {
    dispatch(setCauseAreaAmount(causeArea.id, amount));
    if (activeOrganizations.length <= 1 && activeOrganizations[0]) {
      dispatch(setOrgAmount(activeOrganizations[0].id, amount));
    }
  };
  const handleDistributionChange = value => {
    dispatch(setCauseAreaDistributionType(causeArea.id, value));
    if (value === ShareType.CUSTOM) {
      const hasSetOrgAmounts = activeOrganizations.some(org_0 => (orgAmounts[org_0.id] || 0) > 0);
      if (!hasSetOrgAmounts) {
        const causeAreaAmount = causeAreaAmounts[causeArea.id] || 0;
        for (const org_1 of causeArea.organizations) {
          if (!org_1.standardShare) {
            continue;
          }
          const orgShare = Math.round(org_1.standardShare / 100 * causeAreaAmount);
          dispatch(setOrgAmount(org_1.id, orgShare));
        }
      }
    }
  };
  const T0 = SumButtonsWrapper;
  const t13 = suggestedSums.map(suggested => <div key={suggested.amount}><EffektButton variant={EffektButtonVariant.SECONDARY} selected={inputValue === suggested.amount} onClick={() => {
      plausible("SelectSuggestedSum", {
        props: {
          sum: suggested.amount
        }
      });
      setAmount(suggested.amount);
    }} noMinWidth={true} data-cy={`suggested-sum-${causeArea.id}-${suggested.amount}`}>{`${suggested.amount ? thousandize(suggested.amount) : "-"} kr`}</EffektButton>{suggested.subtext && <i>{suggested.subtext}</i>}</div>);
  let t14;
  if ($[17] !== T0 || $[18] !== t13) {
    t14 = <T0>{t13}</T0>;
    $[17] = T0;
    $[18] = t13;
    $[19] = t14;
  } else {
    t14 = $[19];
  }
  let t15;
  if ($[20] !== amountContext.custom_amount_text) {
    t15 = <label>{amountContext.custom_amount_text}</label>;
    $[20] = amountContext.custom_amount_text;
    $[21] = t15;
  } else {
    t15 = $[21];
  }
  const t16 = `sum-${causeArea.id}`;
  const t17 = inputValue > 0 ? inputValue : "";
  const t18 = `donation-sum-input-${causeArea.id}`;
  let t19;
  if ($[22] !== setAmount) {
    t19 = values => setAmount(values.floatValue ?? 0);
    $[22] = setAmount;
    $[23] = t19;
  } else {
    t19 = $[23];
  }
  let t20;
  if ($[24] !== t16 || $[25] !== t17 || $[26] !== t18 || $[27] !== t19) {
    t20 = <span><NumericFormat name={t16} thousandSeparator=" " allowNegative={false} decimalScale={0} type="tel" placeholder="0" value={t17} autoComplete="off" data-cy={t18} onValueChange={t19} /></span>;
    $[24] = t16;
    $[25] = t17;
    $[26] = t18;
    $[27] = t19;
    $[28] = t20;
  } else {
    t20 = $[28];
  }
  let t21;
  if ($[29] !== t15 || $[30] !== t20) {
    t21 = <SumWrapper>{t15}{t20}</SumWrapper>;
    $[29] = t15;
    $[30] = t20;
    $[31] = t21;
  } else {
    t21 = $[31];
  }
  let t22;
  if ($[32] !== t14 || $[33] !== t21) {
    t22 = <>{t14}{t21}</>;
    $[32] = t14;
    $[33] = t21;
    $[34] = t22;
  } else {
    t22 = $[34];
  }
  const amountInput = t22;
  let t23;
  if ($[35] === Symbol.for("react.memo_cache_sentinel")) {
    t23 = <PaneTitle><wbr /></PaneTitle>;
    $[35] = t23;
  } else {
    t23 = $[35];
  }
  const t24 = !enableSingle;
  let t25;
  if ($[36] !== singleDonationText || $[37] !== t24) {
    t25 = {
      title: singleDonationText,
      value: RecurringDonation.NON_RECURRING,
      data_cy: "radio-single",
      disabled: t24
    };
    $[36] = singleDonationText;
    $[37] = t24;
    $[38] = t25;
  } else {
    t25 = $[38];
  }
  const t26 = !enableRecurring;
  let t27;
  if ($[39] !== monthlyDonationText || $[40] !== t26) {
    t27 = {
      title: monthlyDonationText,
      value: RecurringDonation.RECURRING,
      data_cy: "radio-recurring",
      disabled: t26
    };
    $[39] = monthlyDonationText;
    $[40] = t26;
    $[41] = t27;
  } else {
    t27 = $[41];
  }
  let t28;
  if ($[42] !== t25 || $[43] !== t27) {
    t28 = [t25, t27];
    $[42] = t25;
    $[43] = t27;
    $[44] = t28;
  } else {
    t28 = $[44];
  }
  let t29;
  if ($[45] !== dispatch) {
    t29 = value_0 => dispatch(setRecurring(value_0 as RecurringDonation));
    $[45] = dispatch;
    $[46] = t29;
  } else {
    t29 = $[46];
  }
  let t30;
  if ($[47] !== recurring || $[48] !== t28 || $[49] !== t29) {
    t30 = <RadioButtonGroup options={t28} selected={recurring} onSelect={t29} />;
    $[47] = recurring;
    $[48] = t28;
    $[49] = t29;
    $[50] = t30;
  } else {
    t30 = $[50];
  }
  const t31 = hideAmountInput ? 0 : "auto";
  const t32 = !hasMultipleOrgs;
  let t33;
  if ($[51] !== amountInput || $[52] !== t32) {
    t33 = <AmountInputSection $padBottom={t32}>{amountInput}</AmountInputSection>;
    $[51] = amountInput;
    $[52] = t32;
    $[53] = t33;
  } else {
    t33 = $[53];
  }
  let t34;
  if ($[54] !== t31 || $[55] !== t33) {
    t34 = <AnimateHeight height={t31} animateOpacity={true} duration={300}>{t33}</AnimateHeight>;
    $[54] = t31;
    $[55] = t33;
    $[56] = t34;
  } else {
    t34 = $[56];
  }
  let t35;
  if ($[57] !== activeOrganizations || $[58] !== dispatch || $[59] !== displayOrgAmount || $[60] !== distributionType || $[61] !== handleDistributionChange || $[62] !== hasMultipleOrgs || $[63] !== hasPrefilledOrgs || $[64] !== isMobile || $[65] !== prefilledShares || $[66] !== showAllOrganizations || $[67] !== smartDistributionContext) {
    t35 = hasMultipleOrgs && <ShareSelectionSpacer><RadioButtonGroup options={[{
        title: smartDistributionContext.smart_distribution_radiobutton_text,
        value: ShareType.STANDARD,
        data_cy: "radio-smart-share"
      }, {
        title: smartDistributionContext.custom_distribution_radiobutton_text,
        value: ShareType.CUSTOM,
        data_cy: "radio-custom-share"
      }]} selected={distributionType} onSelect={value_1 => handleDistributionChange(value_1 as ShareType)} /><AnimateHeight height={distributionType === ShareType.STANDARD && smartDistributionContext.smart_distribution_description ? "auto" : 0} animateOpacity={true} duration={300}><InfoParagraph>{smartDistributionContext.smart_distribution_description && <PortableText value={smartDistributionContext.smart_distribution_description} />}</InfoParagraph></AnimateHeight><AnimateHeight height={distributionType === ShareType.CUSTOM ? "auto" : 0} animateOpacity={true} duration={300}><SharesSelectorContainer><ShareSelectionWrapper><ShareContainer>{(hasPrefilledOrgs && !showAllOrganizations ? activeOrganizations.filter(org_2 => prefilledShares?.[org_2.id] !== undefined) : activeOrganizations).map(org_3 => <ShareInputContainer key={org_3.id}><div><ShareLink href={org_3.informationUrl} target={isMobile ? "_blank" : undefined} rel={isMobile ? "noopener noreferrer" : undefined}>{org_3.widgetDisplayName || org_3.name}</ShareLink>{org_3.widgetContext && <ToolTip text={org_3.widgetContext} />}</div><NumericFormat id={`org-${org_3.id}`} aria-label={org_3.widgetDisplayName || org_3.name} type="tel" placeholder="0" value={displayOrgAmount(org_3.id) || ""} step={1} decimalScale={0} allowNegative={false} thousandSeparator=" " autoComplete="off" data-cy={`org-${org_3.id}`} onValueChange={(values_0, sourceInfo) => {
                  if (sourceInfo.source !== "event") {
                    return;
                  }
                  if (prefilledShares?.[org_3.id] !== undefined) {
                    dispatch(setHasManuallyEditedPrefilledOrgAmount(true));
                  }
                  dispatch(setOrgAmount(org_3.id, values_0.floatValue ?? 0));
                }} /></ShareInputContainer>)}</ShareContainer>{hasPrefilledOrgs && !showAllOrganizations && <ShowAllOrganizationsLink type="button" onClick={() => dispatch(setShowAllOrganizations(true))} data-cy="show-all-organizations-button">{smartDistributionContext.show_all_organizations_text} ↓</ShowAllOrganizationsLink>}</ShareSelectionWrapper></SharesSelectorContainer></AnimateHeight></ShareSelectionSpacer>;
    $[57] = activeOrganizations;
    $[58] = dispatch;
    $[59] = displayOrgAmount;
    $[60] = distributionType;
    $[61] = handleDistributionChange;
    $[62] = hasMultipleOrgs;
    $[63] = hasPrefilledOrgs;
    $[64] = isMobile;
    $[65] = prefilledShares;
    $[66] = showAllOrganizations;
    $[67] = smartDistributionContext;
    $[68] = t35;
  } else {
    t35 = $[68];
  }
  let t36;
  if ($[69] !== t30 || $[70] !== t34 || $[71] !== t35) {
    t36 = <div>{t23}{t30}{t34}{t35}</div>;
    $[69] = t30;
    $[70] = t34;
    $[71] = t35;
    $[72] = t36;
  } else {
    t36 = $[72];
  }
  const t37 = showErrors && effectiveTotalAmount <= 0;
  let t38;
  if ($[73] !== currentAmount || $[74] !== dispatch || $[75] !== effectiveTotalAmount || $[76] !== isTrackingPrefilledShares || $[77] !== prefilledShares || $[78] !== setShowErrors) {
    t38 = () => {
      if (effectiveTotalAmount <= 0) {
        setShowErrors(true);
        return;
      }
      if (isTrackingPrefilledShares && prefilledShares) {
        Object.entries(prefilledShares).forEach(t39 => {
          const [orgId_0, share_0] = t39;
          dispatch(setOrgAmount(Number(orgId_0), Math.round(share_0 / 100 * currentAmount)));
        });
      }
      dispatch(setSum(effectiveTotalAmount));
      dispatch(nextPane());
    };
    $[73] = currentAmount;
    $[74] = dispatch;
    $[75] = effectiveTotalAmount;
    $[76] = isTrackingPrefilledShares;
    $[77] = prefilledShares;
    $[78] = setShowErrors;
    $[79] = t38;
  } else {
    t38 = $[79];
  }
  let t39;
  if ($[80] !== nextButtonText || $[81] !== t37 || $[82] !== t38) {
    t39 = <ActionBar><NextButton disabled={t37} onClick={t38} data-cy="next-button">{nextButtonText}</NextButton></ActionBar>;
    $[80] = nextButtonText;
    $[81] = t37;
    $[82] = t38;
    $[83] = t39;
  } else {
    t39 = $[83];
  }
  let t40;
  if ($[84] !== t36 || $[85] !== t39) {
    t40 = <Pane><PaneContainer>{t36}{t39}</PaneContainer></Pane>;
    $[84] = t36;
    $[85] = t39;
    $[86] = t40;
  } else {
    t40 = $[86];
  }
  return t40;
};
function _temp(state) {
  return state.layout.causeAreas;
}
function _temp2(state_0) {
  return state_0.donation;
}
function _temp3(ca) {
  return ca.isActive;
}
function _temp4(org) {
  return org.isActive;
}
function _temp5(a, b) {
  return a.ordering - b.ordering;
}